import { useRef } from "react";
import html2canvas from "html2canvas";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareCard({ pledge, onClose }) {
  const cardRef = useRef(null);

  const generate = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return canvas;
  };

  const download = async () => {
    const canvas = await generate();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `50states-sober-${(pledge.name || "story").toLowerCase().replace(/\s+/g, "-")}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Card downloaded — share it anywhere.");
    });
  };

  const nativeShare = async () => {
    const canvas = await generate();
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `50states-sober.png`, { type: "image/png" });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "50 States Sober · Wall of Hope",
            text: `${pledge.name} — ${pledge.days} days sober. Sobriety is possible.`,
          });
        } else {
          download();
        }
      } catch (err) {
        // AbortError = user cancelled the share sheet, silent fallback for anything else
        if (err && err.name !== "AbortError") {
          download();
        }
      }
    });
  };

  return (
    <div data-testid="share-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--bg)] rounded-sm w-full max-w-md p-6 relative">
        <button data-testid="share-close" onClick={onClose} className="absolute top-3 right-3 text-[var(--ink-soft)] hover:text-[var(--ink)] z-10">
          <X className="w-5 h-5" />
        </button>

        <div className="overline mb-4">Share this story</div>

        {/* Card preview - this gets rendered as image */}
        <div ref={cardRef} className="aspect-[4/5] w-full relative overflow-hidden rounded-sm" style={{ background: "var(--forest)" }}>
          <div className="absolute inset-0 grain opacity-40" />
          {/* large day count watermark */}
          <div className="absolute -right-6 -top-6 font-serif-display text-[14rem] leading-none text-white/[0.06] select-none pointer-events-none">
            {pledge.days}
          </div>
          <div className="relative h-full w-full p-7 flex flex-col text-white">
            <div className="flex items-center gap-3">
              <img src="/logo.webp" alt="" className="w-10 h-10 rounded-full object-cover bg-black" />
              <div className="leading-tight">
                <div className="font-serif-display text-base">50 States Sober</div>
                <div className="font-mono-acc text-[9px] tracking-[0.2em] uppercase text-[var(--sand)]">Ask me how</div>
              </div>
            </div>

            <div className="my-auto py-6">
              <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[var(--sand)] mb-3">On the wall of hope</div>
              <div className="font-serif-display text-3xl leading-tight mb-2">
                {pledge.name}{pledge.state && <span className="text-white/60 text-xl font-sans"> · {pledge.state}</span>}
              </div>
              <div className="flex items-baseline gap-3 mb-5">
                <span className="font-serif-display text-7xl text-[var(--sand)] leading-none">{pledge.days}</span>
                <span className="text-xs font-mono-acc tracking-[0.2em] uppercase text-white/60">days sober</span>
              </div>
              {pledge.note && (
                <div className="text-sm italic text-white/85 leading-relaxed border-l-2 border-[var(--terracotta)] pl-3 max-w-[90%]">
                  &ldquo;{pledge.note}&rdquo;
                </div>
              )}
            </div>

            <div className="font-serif-display italic text-[var(--sand)] text-lg">
              Sobriety is possible.
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button data-testid="share-download" onClick={download}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--bg)] px-4 py-3 rounded-sm text-sm hover:bg-[var(--forest)] transition">
            <Download className="w-4 h-4" /> Download PNG
          </button>
          <button data-testid="share-native" onClick={nativeShare}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[var(--terracotta)] text-[var(--ink)] px-4 py-3 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] transition">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>
    </div>
  );
}
